import {
  deriveProjectNameFromRequirement,
  formatDeliverableDate,
  formatDeliverableStem,
  isNonSubjectName,
  programFileExtension,
} from '@realplc/types';
import type { StructuredRequirement } from '../../types';
import {
  compactFilenamePart,
  extractSummaryTldr,
} from '../messageDisplay';

/**
 * 一次交付的名字，算一次，各处只负责格式化。
 *
 * 在此之前名字是被当作数据结构用的字符串：上游拼成 `YYYYMMDD_HHMMSS_<slug>`，下游
 * 再用正则把日期和校验状态反解出来重拼（deliverablePackage）、拆词干拼兄弟文件名
 * （artifactPolicy）、另起一套模板串（EngineeringWorkspace）。任何格式变更都要同步
 * 改四处互不相干的正则，且没有机制保证它们同步。
 *
 * 本模块是唯一允许拼装交付文件名的地方。
 */
export interface DeliverableName {
  /** 中文原样，给侧边栏与界面标题 */
  projectName: string;
  /** 文件系统安全的词干。新回合是英文主语（`ConveyorControl`），历史消息是原来的派生名。 */
  slug: string;
  /** 'YYYYMMDD' */
  date: string;
  major: number;
  minor: number;
  validated: boolean;
  /** 程序文件扩展名，含点：'.scl' / '.st' / '.lad'。西门子叫 SCL，其余叫 ST；含梯形图块的西门子程序叫 LAD。 */
  extension: string;
}

export interface DeliverableNameInput {
  requirement?: Partial<StructuredRequirement>;
  summary?: string | null;
  codeVersionLabel?: string;
  createdAt: number;
  validated: boolean;
  /** 省略时按 requirement 的 brand 与 code 的内容推导（programFileExtension）。 */
  extension?: string;
  /** 交付的程序正文。扩展名看内容：选了梯形图不等于写出了梯形图。 */
  code?: string | null;
}

const FALLBACK_SLUG = 'plc_program';

function parseVersionLabel(label: string | undefined): { major: number; minor: number } {
  const match = (label || '').trim().match(/^[vV](\d+)(?:\.(\d+))?$/);
  if (!match) return { major: 1, minor: 0 };
  return { major: Number(match[1]), minor: Number(match[2] ?? 0) };
}

/**
 * 兜底链。第 2 项起与 MessageBubble.buildProgramDownloadFilename 的历史行为逐项一致，
 * projectName 是新增在最前面的一级——改动其余顺序会静默改变全部历史消息的文件名。
 */
function pickProjectName(input: DeliverableNameInput): string {
  const fromAnalysis = (input.requirement?.projectName || '').trim();
  if (fromAnalysis) return fromAnalysis;

  if (input.summary) {
    const tldr = extractSummaryTldr(input.summary);
    if (tldr) return compactFilenamePart(tldr);
  }

  // 被控对象。结构化抽取出来的名词，比整句话更像名字。
  const target = compactFilenamePart(input.requirement?.desiredOutputs?.[0]);
  if (target) return target;

  // 需求原文首句。
  //
  // 这里原本是 taskType / controlStrategy 的分类标签——它们说的是这份需求属于哪一
  // 类，不是这份需求讲什么。一份「双泵轮换供水」的需求，只因末尾一句「压力低于设定
  // 值 10 秒未恢复则报警」被正则分类器判成 alarm-handling，于是交付件、说明文档和
  // 交付包全部叫「报警_20260903_V1」。分类标签永远不该命名交付件，所以这一级整级
  // 去掉了，换成首句——首句讲的一定是这件事本身。
  const derived = deriveProjectNameFromRequirement(
    input.requirement?.rawPrompt || input.requirement?.normalizedPrompt,
  );
  if (derived) return derived;

  // 末级同样要过主语检验。服务端挡下「继续」后 projectName 是空的，兜底链一路滑到
  // 这里，而 normalizedPrompt 原样还是「继续」——不在这里拦，上一级的守卫就只是把
  // 塌陷点往后挪了一格。
  const last = compactFilenamePart(
    input.requirement?.normalizedPrompt
    || input.requirement?.desiredOutputs?.[0]
    || input.requirement?.entities?.[0]?.name,
  );
  return isNonSubjectName(last) ? '' : last;
}

export function buildDeliverableName(input: DeliverableNameInput): DeliverableName {
  const projectName = pickProjectName(input);
  const { major, minor } = parseVersionLabel(input.codeVersionLabel);
  return {
    projectName,
    // 文件名词干优先用 worker 本轮算好的英文主语：它同时命名桌面上的 .project，
    // 两边读同一个字段才不会各说各话。2026-09-21 工程师拿到的是
    // 带启动自保持_20260921_V1_UNVALIDATED.st 和 V1_0_Constants_v1.project。
    // 没有它的只有历史消息，它们的词干保持原样——已经下载走的文件不改名。
    slug: input.requirement?.projectSlug?.trim()
      || compactFilenamePart(projectName)
      || FALLBACK_SLUG,
    date: formatDeliverableDate(input.createdAt),
    major,
    minor,
    validated: input.validated,
    extension: input.extension
      || programFileExtension({ vendor: input.requirement?.brand, code: input.code }),
  };
}

/** 屏幕显示用，始终保留次版本号：'V2.0' */
export function formatVersionLabel(name: DeliverableName): string {
  return `V${name.major}.${name.minor}`;
}

function stem(name: DeliverableName): string {
  return formatDeliverableStem(name);
}

/**
 * 交付包词干，不含 `_UNVALIDATED` 与 `.zip`。
 *
 * 送给服务端拼最终包名：裁决后缀由服务端的 ReleaseGate 决定，客户端的
 * validationPassed 在那边只算断言、不算证据，所以这里不能把后缀一起送过去。
 */
export function formatArchiveStem(name: DeliverableName): string {
  return stem(name);
}

/** _UNVALIDATED 紧贴扩展名：它是警告而非描述，放最显眼的位置。 */
function unvalidatedSuffix(name: DeliverableName): string {
  return name.validated ? '' : '_UNVALIDATED';
}

export function formatProgramFile(name: DeliverableName): string {
  return `${stem(name)}${unvalidatedSuffix(name)}${name.extension}`;
}

export function formatSiblingFile(
  name: DeliverableName,
  label: string,
  extension: string,
): string {
  return `${stem(name)}_${label}${unvalidatedSuffix(name)}${extension}`;
}

export function formatArchive(name: DeliverableName): string {
  return `${stem(name)}${unvalidatedSuffix(name)}.zip`;
}
